// NodeMissionTaskAttachments.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Services
import { subscribeToChildNodes } from 'services/nodes/NodeReadService';

// Utilities
import {
  filterWorkflowStepAttachmentNodes,
  getWorkflowStepAttachmentPresentation,
  partitionWorkflowStepAttachments,
} from 'node/missions/mission/utils/workflowStepAttachmentUtils';
import { normalizeTypeKeyList } from 'utilities/nodeTypeRelatedLists';
import { getTypeIconProps, resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import './NodeMissionTaskAttachments.css';

// Components
import Icon from 'svg/Icon';
import NodeMissionTaskAttachmentPhotoThumb from 'node/missions/mission/task/attachments/NodeMissionTaskAttachmentPhotoThumb';

/**
 * NodeMissionTaskAttachments
 *
 * Expand/collapse related-node attachments on a workflow step TASK. Lists only
 * Related Types (suggestedChildren) for the host TASK type. Opens items in Peek.
 * PHOTO and SKETCH nodes render as separate thumbnail rows; other types as titled rows.
 *
 * @param {object} stepNode - Linked TASK node for the people step.
 * @param {object|null} user - Current signed-in user.
 * @returns {ReactNode}
 */
const NodeMissionTaskAttachments = React.memo(({
  stepNode,
  user = null,
}) => {
  const { theme } = useTheme();
  const { getType } = useNodeTypes();
  const { openNode } = useNodeOpenIntent();

  const [expanded, setExpanded] = useState(true);
  const [relatedNodes, setRelatedNodes] = useState([]);

  // Same Related Types allowlist as Attach / add menus on this host TASK.
  const relatedTypeKeys = useMemo(() => {
    const hostType = getType(stepNode?.type);
    const configured = normalizeTypeKeyList(hostType?.suggestedChildren);
    return configured.length > 0 ? configured : null;
  }, [getType, stepNode?.type]);

  useEffect(() => {
    const nodeKey = String(stepNode?.key || '').trim();
    const baseKey = String(stepNode?.baseKey || '').trim();

    setRelatedNodes([]);

    if (!nodeKey || !user || !baseKey) {
      return undefined;
    }

    return subscribeToChildNodes(nodeKey, user, stepNode, (children) => {
      setRelatedNodes(Array.isArray(children) ? children : []);
    });
  }, [stepNode, user]);

  const attachments = useMemo(
    () => filterWorkflowStepAttachmentNodes(relatedNodes, relatedTypeKeys),
    [relatedNodes, relatedTypeKeys],
  );

  const { photos, sketches, others } = useMemo(
    () => partitionWorkflowStepAttachments(attachments),
    [attachments],
  );

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleOpenAttachment = useCallback((node) => {
    if (!node?.key) return;
    openNode(node, { preferPeek: true });
  }, [openNode]);

  const renderThumbRow = (nodes, typeKey) => {
    if (!nodes.length) return null;

    const { iconKey, iconPath } = getTypeIconProps(getType(typeKey));
    const fallbackIcon = typeKey === 'SKETCH' ? 'draw' : 'photo';

    return (
      <div className="node-mission-task-attachments-thumb-row">
        <span className="node-mission-task-attachments-item-icon" aria-hidden="true">
          {iconPath ? (
            <Icon
              color={theme.foregroundMuted}
              height={16}
              path={iconPath}
              width={16}
            />
          ) : (
            <Icon
              color={theme.foregroundMuted}
              height={16}
              iconKey={iconKey || fallbackIcon}
              width={16}
            />
          )}
        </span>
        <div className="node-mission-task-attachments-photo-grid">
          {nodes.map((node) => (
            <NodeMissionTaskAttachmentPhotoThumb
              key={node.key}
              node={node}
              onClick={handleOpenAttachment}
            />
          ))}
        </div>
      </div>
    );
  };

  if (!stepNode?.key) {
    return null;
  }

  const countLabel = String(attachments.length);
  const hasAttachments = attachments.length > 0;

  return (
    <div className="node-mission-task-attachments">

      {/* TOGGLE */}
      <button
        className="node-mission-task-attachments-toggle"
        style={{ color: theme.foregroundMuted }}
        type="button"
        onClick={handleToggleExpanded}
      >
        <Icon
          color={theme.foregroundMuted}
          height={18}
          iconKey={expanded ? 'keyboard_arrow_down' : 'chevron_right'}
          width={18}
        />
        <span className="node-mission-task-attachments-toggle-label">
          Attachments
        </span>
        <span className="node-mission-task-attachments-rule" aria-hidden="true" />
        <span className="node-mission-task-attachments-count">
          {countLabel}
        </span>
      </button>

      {/* ITEMS */}
      {expanded ? (
        <div className="node-mission-task-attachments-items">
          {hasAttachments ? (
            <>
              {renderThumbRow(photos, 'PHOTO')}
              {renderThumbRow(sketches, 'SKETCH')}

              {others.map((attachment) => {
                const { title, snippet } = getWorkflowStepAttachmentPresentation(attachment);
                const titleAttr = snippet ? `${title} — ${snippet}` : title;
                const typeDef = getType(attachment?.type) || null;
                const { iconKey, iconPath } = resolveNodeIcon({
                  node: attachment,
                  typeDef,
                });

                return (
                  <button
                    key={attachment.key}
                    className="node-mission-task-attachments-item"
                    style={{ color: theme.foreground }}
                    title={titleAttr}
                    type="button"
                    onClick={() => handleOpenAttachment(attachment)}
                  >

                    {/* ITEM ICON */}
                    <span className="node-mission-task-attachments-item-icon">
                      {iconPath ? (
                        <Icon
                          color={theme.foregroundMuted}
                          height={16}
                          path={iconPath}
                          width={16}
                        />
                      ) : (
                        <Icon
                          color={theme.foregroundMuted}
                          height={16}
                          iconKey={iconKey}
                          width={16}
                        />
                      )}
                    </span>

                    {/* ITEM LABEL */}
                    <span className="node-mission-task-attachments-item-label">
                      <span className="node-mission-task-attachments-item-title">
                        {title}
                      </span>
                      {snippet ? (
                        <span
                          className="node-mission-task-attachments-item-snippet"
                          style={{ color: theme.foregroundMuted }}
                        >
                          {snippet}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </>
          ) : (
            <div
              className="node-mission-task-attachments-empty"
              style={{ color: theme.foregroundMuted }}
            >
              No attachments yet.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
});

NodeMissionTaskAttachments.displayName = 'NodeMissionTaskAttachments';

export default NodeMissionTaskAttachments;
